<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { usePageSeo } from '@/composables/usePageSeo'

usePageSeo({
  title: 'Refund Policy',
  description: 'Papersnap refund policy and how to request a refund.',
  path: '/refund',
})

const sections = [
  { id: 'free', n: '01', label: 'Free tier' },
  { id: 'cancellation', n: '02', label: 'Cancellation' },
  { id: 'auto-renewal', n: '03', label: 'Auto-renewal refunds' },
  { id: 'annual', n: '04', label: 'Annual plans' },
  { id: 'request', n: '05', label: 'How to request' },
  { id: 'disputes', n: '06', label: 'Disputes & chargebacks' },
  { id: 'changes', n: '07', label: 'Changes' },
  { id: 'contact', n: '08', label: 'Contact us' },
]

const activeId = ref('free')
let observer = null

onMounted(() => {
  const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean)
  observer = new IntersectionObserver(
    (entries) => entries.forEach((e) => { if (e.isIntersecting) activeId.value = e.target.id }),
    { rootMargin: '-90px 0px -60% 0px', threshold: 0 },
  )
  els.forEach((el) => observer.observe(el))
})
onBeforeUnmount(() => observer?.disconnect())

function go(id) {
  const el = document.getElementById(id)
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 84, behavior: 'smooth' })
}
</script>

<template>
  <section class="legal-page py-12">
    <div class="priv">
      <div class="priv-head">
        <span class="priv-eyebrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/></svg>
          Legal
        </span>
        <h1>Refund Policy</h1>
        <div class="priv-sub">
          <span class="updated-pill">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            Last updated June 15, 2026
          </span>
          <span class="who">Operated by <b>Keelara LLC</b></span>
        </div>
      </div>

      <div class="lead">
        <span class="lic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M9 12l2 2 4-4"/></svg></span>
        <p>Keelara LLC ("we," "us," or "our") wants you to be satisfied with Papersnap. This Refund Policy explains <b>when and how you can request a refund</b> for paid subscriptions.</p>
      </div>

      <div class="priv-grid">
        <aside class="toc">
          <p class="toc-label">On this page</p>
          <nav>
            <a v-for="s in sections" :key="s.id" :href="`#${s.id}`" :class="{ active: activeId === s.id }" @click.prevent="go(s.id)"><span class="tn">{{ s.n }}</span>{{ s.label }}</a>
          </nav>
        </aside>

        <div class="priv-content">
          <section class="sec" id="free">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></span>
              <h2>Free tier</h2>
            </div>
            <p>The Free tier of Papersnap is provided at no cost and is <b>not subject to this Refund Policy</b>. You may stop using the Free tier at any time without notice.</p>
          </section>

          <section class="sec" id="cancellation">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></span>
              <h2>Subscription cancellation</h2>
            </div>
            <p>You can cancel your subscription at any time from the <b>Settings</b> page within Papersnap. After cancellation, your subscription remains active and you keep access to your paid features until the end of your current paid billing period.</p>
            <p>After that date, your account reverts to the Free tier again and you will not be charged again.</p>
          </section>

          <section class="sec" id="auto-renewal">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M21 21v-5h-5"/></svg></span>
              <h2>Auto-renewal refunds</h2>
            </div>
            <p>Charges from automatic subscription renewals are <b>not eligible for a refund</b> except where required by applicable law.</p>
            <div class="keypoint warn">
              <span class="kic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
              <p>To avoid auto-renewal charges, cancel your subscription <b>at least one day before your renewal date</b>.</p>
            </div>
          </section>

          <section class="sec" id="annual">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>
              <h2>Annual plans</h2>
            </div>
            <p>Annual subscriptions are <b>non-refundable</b> except where required by applicable law. If you cancel an annual subscription, you keep access until the end of the prepaid year and will not be charged for the following year.</p>
          </section>

          <section class="sec" id="request">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></span>
              <h2>How to request a refund</h2>
            </div>
            <p>To request a refund, contact us at <a class="link" href="mailto:contact@keelara.com">contact@keelara.com</a> and include:</p>
            <div class="items">
              <div class="item"><span class="inum">1</span><span class="itxt">The <b>order ID, transaction reference, or invoice number</b> from your purchase confirmation email.</span></div>
              <div class="item"><span class="inum">2</span><span class="itxt">The <b>date and amount</b> of the charge you would like reviewed.</span></div>
              <div class="item"><span class="inum">3</span><span class="itxt">The <b>plan</b> (for example, Papersnap Solo, Business, or Team — monthly or annual).</span></div>
              <div class="item"><span class="inum">4</span><span class="itxt">A short <b>explanation of the reason</b> for your request.</span></div>
            </div>
            <div class="timeline">
              <div class="tl">
                <span class="tlic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg></span>
                <div>
                  <div class="big">2 <span>business days</span></div>
                  <div class="lab">to acknowledge your request</div>
                </div>
              </div>
              <div class="tl">
                <span class="tlic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></span>
                <div>
                  <div class="big">7 <span>business days</span></div>
                  <div class="lab">to reach a final decision</div>
                </div>
              </div>
            </div>
            <p style="margin-top: 14px;">Each request is reviewed individually against this Refund Policy and any consumer-protection rights that apply in your jurisdiction. If we are unable to grant a refund, we will explain why and discuss any alternatives that may help, such as cancelling auto-renewal so you are not billed again.</p>
          </section>

          <section class="sec" id="disputes">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></span>
              <h2>Disputes and chargebacks</h2>
            </div>
            <p>Chargebacks are slower and more disruptive than a direct refund.</p>
            <div class="timeline one">
              <div class="tl">
                <span class="tlic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
                <div>
                  <div class="big">30–90 <span>days to resolve</span></div>
                  <div class="lab">vs. a few business days for a direct refund</div>
                </div>
              </div>
            </div>
            <p style="margin-top: 14px;">Our payment provider charges us a fee for each disputed transaction, and we may be required to respond with supporting documentation. While a chargeback is open, your subscription may be suspended pending the outcome.</p>
            <p>If a chargeback is filed <b>without a prior good-faith attempt to contact us</b>, or is determined by the payment network to be without merit, we reserve the right to:</p>
            <div class="reserve">
              <div class="res-item"><span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg></span><span class="rtxt"><b>Suspend or terminate</b> any active subscriptions associated with the disputed payment.</span></div>
              <div class="res-item"><span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span><span class="rtxt"><b>Block future purchases</b> from the same email address, payment method, or device.</span></div>
              <div class="res-item"><span class="ric"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span><span class="rtxt"><b>Recover the chargeback fee</b> and any other costs we incur as a result of the dispute, where permitted by law.</span></div>
            </div>
          </section>

          <section class="sec" id="changes">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg></span>
              <h2>Changes to this policy</h2>
            </div>
            <p>We may update this Refund Policy from time to time. The "Last updated" date at the top of the page reflects the most recent revision. <b>Continued use of paid features after changes have been posted constitutes acceptance of the updated policy.</b></p>
          </section>

          <section class="sec contact" id="contact">
            <div class="sec-head">
              <span class="sec-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg></span>
              <h2>Contact us</h2>
            </div>
            <p>Questions about this Refund Policy? Contact us at:</p>
            <div class="contact-row">
              <a class="mail-btn" href="mailto:contact@keelara.com">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/><polyline points="22 6 12 13 2 6"/></svg>
                contact@keelara.com
              </a>
              <span class="contact-note">Papersnap is operated by <b>Keelara LLC</b>.</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.legal-page {
  --fg-1: #0f172a; --fg-2: #334155; --fg-3: #475569; --fg-4: #64748b;
  --panel: #ffffff; --sunk: #f8fafc;
  --bd-1: #e2e8f0; --bd-2: #cbd5e1;
  --accent: rgb(var(--color-primary));
  --accent-soft: rgb(var(--color-primary) / 0.10);
  --amber-ic: #d97706; --amber-fg: #b45309;
  --red-ic: #dc2626;
}
.dark .legal-page {
  --fg-1: #f8fafc; --fg-2: #cbd5e1; --fg-3: #94a3b8; --fg-4: #64748b;
  --panel: #0f172a; --sunk: rgb(2 6 23 / 0.5);
  --bd-1: #1e293b; --bd-2: #334155;
  --accent: rgb(45 212 191); --accent-soft: rgb(45 212 191 / 0.10);
  --amber-ic: #fcd34d; --amber-fg: #fcd34d;
  --red-ic: #f87171;
}

.priv { max-width: 980px; margin: 0 auto; width: 100%; }
.priv-head { margin: 4px 0 26px; }
.priv-eyebrow { display: inline-flex; align-items: center; gap: 7px; font-family: var(--font-mono); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 12px; }
.priv-eyebrow svg { width: 13px; height: 13px; }
.priv-head h1 { font-family: var(--font-display); font-weight: 700; font-size: 32px; color: var(--fg-1); margin: 0; letter-spacing: -0.01em; }
.priv-sub { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.updated-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 9999px; background: var(--sunk); border: 1px solid var(--bd-2); font-size: 12px; font-weight: 600; color: var(--fg-2); }
.updated-pill svg { width: 12px; height: 12px; color: var(--fg-4); }
.priv-sub .who { font-size: 13px; color: var(--fg-4); }
.priv-sub .who b { color: var(--fg-3); font-weight: 600; }

.lead { display: flex; gap: 14px; align-items: flex-start; padding: 18px 20px; border-radius: 14px; margin-bottom: 28px; background: rgb(var(--color-primary) / 0.07); border: 1px solid rgb(var(--color-primary) / 0.22); }
.lead .lic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.16); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.lead .lic svg { width: 18px; height: 18px; }
.lead p { margin: 0; font-size: 14px; line-height: 1.65; color: var(--fg-2); }
.lead p a { color: var(--accent); font-weight: 600; }
.lead p b { color: var(--fg-1); font-weight: 600; }

.priv-grid { display: grid; grid-template-columns: 210px 1fr; gap: 34px; align-items: start; }
@media (max-width: 860px) { .priv-grid { grid-template-columns: 1fr; gap: 18px; } }
.toc { position: sticky; top: 84px; }
@media (max-width: 860px) { .toc { display: none; } }
.toc-label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--fg-4); margin: 0 0 10px 12px; }
.toc nav { display: flex; flex-direction: column; gap: 1px; }
.toc a { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--fg-3); border-left: 2px solid transparent; transition: all .15s ease; }
.toc a:hover { color: var(--fg-1); background: var(--sunk); }
.toc a.active { color: var(--accent); background: var(--accent-soft); border-left-color: rgb(var(--color-primary)); }
.toc a .tn { font-family: var(--font-mono); font-size: 11px; color: var(--fg-4); width: 16px; flex-shrink: 0; }
.toc a.active .tn { color: var(--accent); }

.priv-content { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.sec { background: var(--panel); border: 1px solid var(--bd-1); border-radius: 15px; padding: 24px 26px; scroll-margin-top: 90px; }
.sec-head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.sec-ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.14); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.sec-ic svg { width: 18px; height: 18px; }
.sec-head h2 { font-family: var(--font-display); font-weight: 600; font-size: 18px; color: var(--fg-1); margin: 0; }
.sec p { font-size: 14px; line-height: 1.65; color: var(--fg-3); margin: 0 0 12px; }
.sec p:last-child { margin-bottom: 0; }
.sec p b { color: var(--fg-1); font-weight: 600; }
.sec .link { color: var(--accent); font-weight: 600; }

.keypoint { display: flex; gap: 12px; align-items: flex-start; margin-top: 14px; padding: 13px 16px; border-radius: 11px; }
.keypoint.warn { background: rgb(245 158 11 / 0.08); border: 1px solid rgb(245 158 11 / 0.3); }
.keypoint.warn .kic { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; background: rgb(245 158 11 / 0.16); color: var(--amber-ic); display: inline-flex; align-items: center; justify-content: center; }
.keypoint.warn .kic svg { width: 15px; height: 15px; }
.keypoint p { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); margin: 0; align-self: center; }
.keypoint.warn p b { color: var(--amber-fg); font-weight: 700; }

.timeline { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 4px 0; }
.timeline.one { grid-template-columns: 1fr; }
@media (max-width: 520px) { .timeline { grid-template-columns: 1fr; } }
.tl { display: flex; align-items: center; gap: 14px; padding: 16px 18px; border-radius: 12px; background: var(--sunk); border: 1px solid var(--bd-2); }
.tl .tlic { width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; background: rgb(var(--color-primary) / 0.14); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.tl .tlic svg { width: 18px; height: 18px; }
.tl .big { font-family: var(--font-display); font-weight: 700; font-size: 22px; color: var(--fg-1); line-height: 1.05; }
.tl .big span { font-size: 13px; color: var(--fg-3); font-weight: 600; }
.tl .lab { font-size: 12px; color: var(--fg-4); margin-top: 3px; }

.items { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.item { display: flex; align-items: flex-start; gap: 12px; padding: 11px 15px; border-radius: 11px; background: var(--sunk); border: 1px solid var(--bd-1); }
.item .inum { width: 22px; height: 22px; border-radius: 7px; flex-shrink: 0; margin-top: 1px; font-family: var(--font-mono); font-size: 11px; font-weight: 700; background: rgb(var(--color-primary) / 0.16); color: var(--accent); display: inline-flex; align-items: center; justify-content: center; }
.item .itxt { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); }
.item .itxt b { color: var(--fg-1); font-weight: 600; }

.reserve { display: flex; flex-direction: column; gap: 8px; margin-top: 4px; }
.res-item { display: flex; align-items: flex-start; gap: 11px; padding: 11px 15px; border-radius: 11px; background: rgb(239 68 68 / 0.06); border: 1px solid rgb(239 68 68 / 0.22); }
.res-item .ric { width: 24px; height: 24px; border-radius: 7px; flex-shrink: 0; margin-top: 1px; background: rgb(239 68 68 / 0.14); color: var(--red-ic); display: inline-flex; align-items: center; justify-content: center; }
.res-item .ric svg { width: 14px; height: 14px; }
.res-item .rtxt { font-size: 13.5px; line-height: 1.55; color: var(--fg-2); }
.res-item .rtxt b { color: var(--fg-1); font-weight: 600; }

.sec.contact { background: linear-gradient(180deg, rgb(var(--color-primary) / 0.06), var(--panel)); border-color: rgb(var(--color-primary) / 0.22); }
.contact-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.mail-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 10px; background: rgb(var(--color-primary)); color: #fff; font-weight: 600; font-size: 13.5px; transition: background .15s ease; }
.mail-btn:hover { background: rgb(var(--color-primary) / 0.88); }
.mail-btn svg { width: 15px; height: 15px; }
.contact-note { font-size: 13px; color: var(--fg-4); }
.contact-note b { color: var(--fg-3); font-weight: 600; }
</style>
